import React, { useCallback, useEffect, useRef, useState } from 'react';
import { usePlateEditorState, isCollapsed, ELEMENT_LINK, usePlateEditorRef, focusEditor } from '@udecode/plate';

import OutsideClickHandler from 'components/common/utilities/outside_click_handler';
import { Item } from 'components/text_editor_new/components/controls/controls_new';
import {
  StyledMenu,
  StyledInput,
  StyledLinkMenuWrapper,
  StyledLinkMenuSeparator,
  StyledLinkMenuButton,
} from 'components/composer/shared/link_button_styles';
import { usePopoverMenu } from 'components/common/menu';
import hasPluginOfType from 'components/text_editor_new/lib/has_plugin_of_type';
import KeypressEventHandler from 'components/lib/dom_controls/lib/keypress_event_handler';
import { getNormalizedUrl } from 'components/lib/linkify_it';

const DEFAULT_URL = '';

function BaseLinkButton() {
  const { targetRef, setTargetRef, isOpen, onClose, onToggle } = usePopoverMenu();
  const [shouldDisableSubmitButton, setShouldDisableSubmitButton] = useState(true);
  const [linkUrl, setLinkUrl] = useState(DEFAULT_URL);
  const [isEditing, setIsEditing] = useState(false);
  const inputFieldRef = useRef();

  const editor = usePlateEditorState();

  useEffect(() => {
    if (isOpen && inputFieldRef.current) {
      inputFieldRef.current.focus();
    }
  }, [isOpen]);

  const cleanUp = useCallback(() => {
    if (isOpen) {
      setIsEditing(false);
      setLinkUrl(DEFAULT_URL);
      setShouldDisableSubmitButton(true);
      onClose();

      focusEditor(editor, editor.selection);
    }
  }, [isOpen, onClose, editor]);

  const toggleWhenSelectionExists = () => {
    if (!isCollapsed(editor.selection)) {
      if (editor.isLinkSelected()) {
        const linkNode = editor.getCurrentLinkNode();
        setIsEditing(true);
        setLinkUrl(linkNode[0].url);
      }

      onToggle();
    }
  };

  const onChange = event => {
    if (shouldDisableSubmitButton) {
      setShouldDisableSubmitButton(false);
    }
    setLinkUrl(event.target.value);
  };

  const onClickUpdate = evt => {
    evt.preventDefault();
    const linkNode = editor.getCurrentLinkNode();
    editor.editLink(linkNode, getNormalizedUrl(linkUrl));
    cleanUp();
  };

  const onClickAdd = evt => {
    evt.preventDefault();
    editor.addLink(getNormalizedUrl(linkUrl));
    cleanUp();
  };

  const onClickRemove = () => {
    const linkNode = editor.getCurrentLinkNode();
    // Let normalizer handle removing the link node
    editor.editLink(linkNode, '');
    cleanUp();
  };

  const onSubmit = isEditing ? onClickUpdate : onClickAdd;
  const keypressEventHandler = new KeypressEventHandler(onSubmit, cleanUp, { isInputField: true });

  return (
    <OutsideClickHandler onClickOutside={cleanUp}>
      <Item
        data-aid="link"
        isDisabled={editor.shouldDisableLinkButton()}
        onClick={toggleWhenSelectionExists}
        ref={setTargetRef}
      >
        <i className="fa fa-link" />
      </Item>
      <StyledMenu
        data-aid="link-menu"
        isOpen={isOpen}
        onClickOutside={null}
        onClose={onClose}
        position="bottom"
        targetPosition="center"
        targetRef={targetRef}
      >
        <StyledLinkMenuWrapper>
          <StyledInput
            autoFocus
            onChange={onChange}
            placeholder="Type or paste URL"
            ref={inputFieldRef}
            value={linkUrl}
            {...keypressEventHandler.reactAttributes()}
          />
          <StyledLinkMenuSeparator />
          {isEditing ? (
            <>
              <StyledLinkMenuButton disabled={shouldDisableSubmitButton} onClick={onClickUpdate}>
                Update
              </StyledLinkMenuButton>
              <StyledLinkMenuButton onClick={onClickRemove}>Remove</StyledLinkMenuButton>
            </>
          ) : (
            <StyledLinkMenuButton disabled={shouldDisableSubmitButton} onClick={onClickAdd}>
              Add
            </StyledLinkMenuButton>
          )}
        </StyledLinkMenuWrapper>
      </StyledMenu>
    </OutsideClickHandler>
  );
}

export function LinkButton() {
  const editor = usePlateEditorRef();

  if (!hasPluginOfType(editor, ELEMENT_LINK)) {
    return null;
  }

  return <BaseLinkButton />;
}
